import { useTranslations } from 'next-intl'

import { CopyButton, useFormatCurrency, useFormatDate } from '@/shared/lib'
import { Icon, Description } from '@/shared/ui'

import { DeadlineTimer } from './deadline-timer'

export type Props = {
  payDeadlineTime: string
  vaBankName: string
  vaAccountHolder: string
  vaAccount: string
  finalPayFee: number
}

const DATE_TIME_FORMAT = 'yyyy년 MM월 dd일 a h:mm'

export const AccountPayment = ({
  payDeadlineTime,
  vaBankName,
  vaAccountHolder,
  vaAccount,
  finalPayFee,
}: Props) => {
  const t = useTranslations()

  const { formatDate } = useFormatDate()
  const { formatCurrency } = useFormatCurrency()

  const account = `${vaBankName} ${vaAccount}`

  const formattedPayDeadlineTime = formatDate({
    date: payDeadlineTime,
    format: DATE_TIME_FORMAT,
  })

  return (
    <div className="space-y-6">
      <p className="typo-body1-reading text-center font-semibold text-black">
        {t.rich('lease_detail.make_payment.bank_transfer.notice', {
          strong: chunks => <span className="text-purple-500">{chunks}</span>,
        })}
      </p>

      <div className="rounded-sm shadow-[0px_0px_4px_0px_rgba(0,0,0,0.18)]">
        <div className="flex flex-col items-center justify-center gap-2 bg-gray-50 px-3 py-4 text-center">
          <div className="typo-label1 flex items-center gap-1">
            <Icon name="clock" size={18} className="text-black" />
            <span className="font-medium text-black">
              {t('lease_detail.make_payment.deadline.label')}
            </span>
            <DeadlineTimer deadline={payDeadlineTime} />
          </div>
          <p className="typo-label2 font-normal text-gray-900">
            {t('lease_detail.make_payment.deadline.notice', {
              datetime: formattedPayDeadlineTime,
            })}
          </p>
        </div>

        <div className="p-6">
          <Description.List>
            <Description.Item className="justify-between">
              <Description.ItemLabel>
                {t('lease_detail.make_payment.label.account_number')}
              </Description.ItemLabel>
              <Description.ItemValue className="flex items-center gap-2">
                <span>{account}</span>
                <CopyButton
                  value={account}
                  successMessage={t(
                    'lease_detail.make_payment.toast.account_number_copy_success',
                  )}
                />
              </Description.ItemValue>
            </Description.Item>

            <Description.Item
              className="justify-between"
              label={t('lease_detail.make_payment.label.account_holder')}
              value={vaAccountHolder}
            />

            <Description.Item className="justify-between">
              <Description.ItemLabel className="self-start">
                {t('lease_detail.make_payment.label.amount')}
              </Description.ItemLabel>
              <Description.ItemValue className="flex flex-col items-end gap-1">
                <span className="typo-body1 font-bold text-gray-900">
                  {formatCurrency(finalPayFee)}
                </span>
                <span className="typo-label2 font-normal text-gray-500">
                  {t('lease_detail.make_payment.amount.notice')}
                </span>
              </Description.ItemValue>
            </Description.Item>
          </Description.List>
        </div>
      </div>
    </div>
  )
}
